/**
 * Authentication Context
 *
 * Manages authentication with automatic token refresh.
 * User profile is received from the API response (not decoded from JWT).
 */

import React, {
  createContext,
  ReactNode,
  useEffect,
  useState,
  useRef,
  useContext,
  useCallback,
  useMemo,
} from "react";
import {
  getAuthorizationToken,
  setAuthorizationToken,
  unsetAuthorizationToken,
  getRefreshToken,
  setUserProfile,
  getUserProfile,
  unsetUserProfile,
} from "../global/storage/auth";
import { ApiRefreshToken } from "../services/api";
import { validateToken, isTokenExpiringSoon } from "../utils/tokenValidation";
import { AlertContext } from "./AlertContext";
import { UserProfile } from "../global/types/user";
import { toast } from "sonner";

// ============================================================================
// Types
// ============================================================================

interface AuthProviderProps {
  children: ReactNode;
}

export interface AuthContextProps {
  signed: boolean;
  token: string | null;
  user: UserProfile | null;
  isAdmin: boolean;
  login: (token: string | null, userProfile?: UserProfile | null) => void;
  logout: (reason?: string) => void;
  updateUserProfile: (profile: UserProfile) => void;
}

// ============================================================================
// Constants
// ============================================================================

const TOKEN_CHECK_INTERVAL_MS = 60000;
const SESSION_KEEPALIVE_INTERVAL_MS = 5 * 60 * 1000;
const SESSION_ACTIVITY_WINDOW_MS = 10 * 60 * 1000;
const LOG = "[Auth]";

// ============================================================================
// Context
// ============================================================================

export const AuthContext = createContext<AuthContextProps>(
  {} as AuthContextProps
);

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { setAlertMessage } = useContext(AlertContext);

  const [token, setToken] = useState<string | null>(() =>
    getAuthorizationToken()
  );
  const [user, setUser] = useState<UserProfile | null>(() => getUserProfile());

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isRefreshingRef = useRef(false);
  const initialCheckDoneRef = useRef(false);
  const lastUserActivityRef = useRef(Date.now());
  const lastRefreshAttemptRef = useRef(Date.now());

  // isAdmin now comes from API response (database-backed permissions)
  const isAdmin = user?.isAdmin ?? false;

  // ============================================================================
  // Core Functions
  // ============================================================================

  const logout = useCallback((reason?: string) => {
    console.log(`${LOG} Logout${reason ? `: ${reason}` : ""}`);
    setToken(null);
    setUser(null);
    unsetAuthorizationToken();
    unsetUserProfile();
    initialCheckDoneRef.current = false;
    if (reason) toast.warning(reason);
  }, []);

  const login = useCallback(
    (newToken: string | null, userProfile?: UserProfile | null) => {
      if (!newToken) return;

      console.log(`${LOG} Login with user profile:`, userProfile);

      // Set and persist user profile FIRST
      if (userProfile) {
        setUser(userProfile);
        setUserProfile(userProfile); // PERSIST TO LOCALSTORAGE
        console.log(`${LOG} User isAdmin:`, userProfile.isAdmin);
      }

      setToken(newToken);
      setAuthorizationToken(newToken);
      initialCheckDoneRef.current = true;
      lastUserActivityRef.current = Date.now();
      lastRefreshAttemptRef.current = Date.now();

      console.log(`${LOG} Login successful`);
    },
    []
  );

  const updateUserProfile = useCallback((profile: UserProfile) => {
    setUser(profile);
  }, []);

  const refreshToken = useCallback(
    async (context: string, logoutOnFailure = true): Promise<boolean> => {
      if (isRefreshingRef.current) return false;

      isRefreshingRef.current = true;
      lastRefreshAttemptRef.current = Date.now();

      try {
        console.log(`${LOG} Refreshing token (${context})`);
        const result = await ApiRefreshToken();

        if (result.success && result.data) {
          setToken(result.data.access_token);

          // Update user profile from refresh response
          if (result.data.user) {
            setUser(result.data.user);
            setUserProfile(result.data.user);
          }

          console.log(`${LOG} Token refreshed (${context})`);
          return true;
        }

        console.error(`${LOG} Refresh failed (${context}):`, result.error);
        if (logoutOnFailure) {
          logout(result.error?.userMessage || "Erro ao renovar sessão.");
        }
        return false;
      } catch (error) {
        console.error(`${LOG} Refresh error (${context}):`, error);
        if (logoutOnFailure) {
          logout("Erro inesperado ao renovar sessão.");
        }
        return false;
      } finally {
        isRefreshingRef.current = false;
      }
    },
    [logout]
  );

  // ============================================================================
  // Effects
  // ============================================================================

  // Clear user when token is cleared
  // Note: User profile is now set by the login function from API response
  useEffect(() => {
    if (!token) {
      setUser(null);
    }
  }, [token]);

  // Keep the Keycloak idle session fresh only while the user is active.
  useEffect(() => {
    if (!token) return;

    const recordActivity = () => {
      lastUserActivityRef.current = Date.now();
    };
    const activityEvents = [
      "click",
      "keydown",
      "mousemove",
      "scroll",
      "touchstart",
    ];

    activityEvents.forEach((eventName) => {
      window.addEventListener(eventName, recordActivity, { passive: true });
    });

    return () => {
      activityEvents.forEach((eventName) => {
        window.removeEventListener(eventName, recordActivity);
      });
    };
  }, [token]);

  // Startup validation (runs once)
  useEffect(() => {
    const validate = async () => {
      const accessToken = getAuthorizationToken();
      const refreshTokenStored = getRefreshToken();

      if (!accessToken && !refreshTokenStored) {
        console.log(`${LOG} No tokens on startup`);
        return;
      }

      console.log(`${LOG} Validating tokens on startup`);

      const accessVal = validateToken(accessToken);
      const refreshVal = validateToken(refreshTokenStored);

      // Both expired
      if (accessVal.isExpired && refreshVal.isExpired) {
        console.warn(`${LOG} Both tokens expired`);
        logout("Sua sessão expirou. Por favor, faça login novamente.");
        return;
      }

      // Access expired, refresh valid
      if (accessVal.isExpired && !refreshVal.isExpired) {
        console.log(`${LOG} Access expired, refreshing`);
        await refreshToken("startup-expired", true);
        initialCheckDoneRef.current = true;
        return;
      }

      // Access expiring soon
      if (accessToken && isTokenExpiringSoon(accessToken)) {
        console.log(`${LOG} Token expiring soon`);
        await refreshToken("startup-expiring", false);
      }

      initialCheckDoneRef.current = true;
    };

    validate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Periodic refresh interval
  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    if (!token) return;

    intervalRef.current = setInterval(async () => {
      const currentToken = getAuthorizationToken();
      const now = Date.now();
      const hasRecentActivity =
        now - lastUserActivityRef.current <= SESSION_ACTIVITY_WINDOW_MS;
      const shouldKeepSessionAlive =
        hasRecentActivity &&
        now - lastRefreshAttemptRef.current >= SESSION_KEEPALIVE_INTERVAL_MS;

      if (currentToken && isTokenExpiringSoon(currentToken)) {
        console.log(`${LOG} Periodic check: expiring soon`);
        await refreshToken("periodic", true);
        return;
      }

      if (currentToken && shouldKeepSessionAlive) {
        console.log(`${LOG} Periodic check: keeping active session alive`);
        await refreshToken("keepalive", true);
      }
    }, TOKEN_CHECK_INTERVAL_MS);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // ============================================================================
  // Provider
  // ============================================================================

  const value = useMemo<AuthContextProps>(
    () => ({
      signed: Boolean(token),
      token,
      user,
      isAdmin,
      login,
      logout,
      updateUserProfile,
    }),
    [token, user, isAdmin, login, logout, updateUserProfile]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// ============================================================================
// Hook
// ============================================================================

export const useAuth = (): AuthContextProps => {
  const context = useContext(AuthContext);

  if (!context || Object.keys(context).length === 0) {
    throw new Error("useAuth must be used within AuthProvider");
  }

  return context;
};
